import { Controller } from '@hotwired/stimulus';

/**
 * disclosure-panel — un panneau ouvert par un bouton de divulgation, fermé par Échap, par un clic extérieur
 * ou par son déclencheur. Le contrôleur PARTAGÉ de la refonte planning PR 2 (RV-36) : le sélecteur de
 * couleur (`Form::ColorPickerComponent`, Task 8) l'utilise, panneau FLOTTANT depuis le 16/10 ; les menus
 * « Affichage ▾ » et « Codes ▾ » du Gantt (Task 20) le réutilisent tel quel. Ce contrôleur ne place rien :
 * dans le flux ou flottant, c'est le gabarit de l'appelant qui en décide.
 *
 * L'état initial est rendu par le SERVEUR (`aria-expanded` du déclencheur, attribut `hidden` du panneau) : un
 * panneau rendu ouvert (erreur serveur à montrer) arme ses écouteurs dès `connect`.
 *
 * Échap, contrat de `searchable_select_controller.js:99-111` : pendant que le panneau est ouvert, un
 * écouteur `keydown` en CAPTURE sur `document` consomme Échap AVANT tout ancêtre (`preventDefault` +
 * `stopPropagation`) : il ferme le panneau, jamais le tiroir qui le contient (`slide_over_controller.js`
 * ignore un Échap déjà consommé), et rend le focus au déclencheur. Ajouté à l'ouverture, retiré à la
 * fermeture ET au `disconnect`, avec le MÊME drapeau de capture (il fait partie de l'identité de l'écouteur).
 *
 * Le nœud qui porte `hidden` ne doit porter AUCUNE classe d'affichage (`flex`, `grid`…) : en Tailwind v4
 * elle bat `hidden`. La mise en page vit un niveau plus bas.
 *
 * Quatrième appelant : le popover « Concernés » de la fiche de remarque (`remarks/_panel_concerned`,
 * maquette H-Apercu-A-Popover), un `role="dialog"` NON modal dans le tiroir de la remarque. Il active
 * l'option `refocus-on-outside-click` : un clic extérieur qui laisse le focus NULLE PART (`<body>`, un
 * nœud non tabulable comme le panneau du tiroir en `tabindex="-1"`, ou le panneau qu'on referme) le rend
 * au déclencheur ; un clic sur un autre contrôle (le « Commentaire » juste dessous) lui laisse le focus.
 * Sans l'option, le clic extérieur ne touche jamais au focus (comportement des trois premiers appelants).
 *
 * Targets :
 *   • trigger — le `<button>` dont `aria-expanded` reflète l'état (`aria-controls` = le panneau).
 *   • panel   — le panneau, `hidden` quand il est fermé.
 *
 * Values :
 *   • refocusOnOutsideClick (Boolean, faux par défaut) — voir ci-dessus.
 *
 * Actions :
 *   • toggle — ouvre ou ferme (clic du déclencheur).
 *   • open   — ouvre (événement d'un contrôleur voisin, ex. `color-picker:open`).
 *   • close  — ferme et rend le focus au déclencheur (ex. `color-picker:close`, Entrée sur une radio).
 *
 * @example
 *   <div data-controller="disclosure-panel">
 *     <button type="button" aria-expanded="false" aria-controls="p1"
 *             data-disclosure-panel-target="trigger" data-action="disclosure-panel#toggle">Affichage</button>
 *     <div id="p1" data-disclosure-panel-target="panel" hidden>
 *       <div class="flex gap-2">…</div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ['trigger', 'panel'];
  static values = { refocusOnOutsideClick: Boolean };

  connect() {
    this.boundEscapeGuard = this.escapeGuard.bind(this);
    this.boundClickOutside = this.clickOutside.bind(this);
    if (this.isOpen()) this.#listen();
  }

  disconnect() {
    this.#unlisten();
  }

  isOpen() {
    return this.hasTriggerTarget && this.triggerTarget.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.hasTriggerTarget || !this.hasPanelTarget || this.isOpen()) return;

    this.triggerTarget.setAttribute('aria-expanded', 'true');
    this.panelTarget.hidden = false;
    this.#listen();
  }

  close({ refocus = true } = {}) {
    if (!this.hasTriggerTarget || !this.hasPanelTarget) return;

    const wasOpen = this.isOpen();
    this.triggerTarget.setAttribute('aria-expanded', 'false');
    this.panelTarget.hidden = true;
    this.#unlisten();
    if (wasOpen && refocus) this.triggerTarget.focus();
  }

  escapeGuard(event) {
    if (event.key !== 'Escape' || !this.isOpen()) return;

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  // Un clic HORS de la racine ferme sans voler le focus (il est déjà là où l'utilisateur l'a mis). Avec
  // l'option `refocus-on-outside-click`, un focus resté nulle part revient au déclencheur.
  clickOutside(event) {
    if (this.element.contains(event.target)) return;

    this.close({ refocus: Boolean(this.refocusOnOutsideClickValue) && this.#focusLost() });
  }

  // Nulle part : aucun élément actif, `<body>`, un nœud hors de l'ordre de tabulation (tabindex négatif),
  // ou le panneau qu'on est en train de masquer.
  #focusLost() {
    const active = document.activeElement;
    if (!active || active === document.body || active.tabIndex < 0) return true;

    return this.hasPanelTarget && this.panelTarget.contains(active);
  }

  #listen() {
    document.addEventListener('keydown', this.boundEscapeGuard, true);
    document.addEventListener('click', this.boundClickOutside);
  }

  #unlisten() {
    document.removeEventListener('keydown', this.boundEscapeGuard, true);
    document.removeEventListener('click', this.boundClickOutside);
  }
}
